import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Wind, Cpu, Wrench } from 'lucide-react';
import { EASE, Reveal } from './reveal';
import SectionHead from './SectionHead';
import { track } from '../lib/track';

const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Galvanized Armor',
    body:
      'Galvanized for maximum rust protection — keeping nuts and bolts tighter over time and extending the total life of the hammer.',
  },
  {
    icon: Wind,
    title: 'Pneumatic Lift & Drop',
    body:
      'Uses pneumatic pressure to both lift AND drop the hammer for maximum striking power on every cycle.',
  },
  {
    icon: Cpu,
    title: 'Off-Hammer "Black Box"',
    body:
      'Our proprietary controller is NOT mounted on the hammer itself, so it never gets beat up by the workload it commands.',
  },
  {
    icon: Wrench,
    title: 'Effortless Maintenance',
    body:
      'Easy to service and never gets stuck due to electrical issues — designed to keep working in all conditions.',
  },
];

export default function PneumaticHammer() {
  return (
    <section
      id="hammer"
      data-testid="pneumatic-hammer-section"
      className="relative bg-black border-t border-white/5 py-16 sm:py-20 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute -top-24 -left-24 w-[320px] sm:w-[520px] h-[320px] sm:h-[520px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-16 w-[280px] sm:w-[460px] h-[280px] sm:h-[460px] rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <SectionHead
          eyebrow="Our Pneumatic Pile Driver"
          titleLines={[
            <>The Absolute <span key="accent" className="text-cyan-300">Hammer</span></>,
          ]}
          body="Our proprietary hammer is, like our barge, redesigned to take all issues with modern pneumatic pile drivers into account and simply perform better. It has:"
        />

        <div className="mt-10 sm:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 lg:gap-12 items-stretch">
          {/* Left: Hammer image */}
          <Reveal className="md:col-span-4 relative order-1 min-h-[360px] md:min-h-0" delay={0.2}>
            <div className="hidden md:flex absolute -left-2 top-2 flex-col items-center gap-3 z-10">
              <span className="h-14 w-px bg-gradient-to-b from-cyan-400 to-transparent" />
              <span
                className="font-tech text-[10px] tracking-[0.32em] text-cyan-300/80 uppercase"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
              >
                Proprietary · Built In-House
              </span>
            </div>

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[45%] h-[45%] rounded-full bg-cyan-500/10 blur-[70px]" />
            </div>

            <div className="md:absolute md:inset-0 flex items-center justify-center h-full">
              <img
                src="/assets/absolute_hammer.webp"
                alt="The Absolute Hammer — proprietary pneumatic pile driving hammer"
                className="relative max-h-[440px] w-auto md:max-h-none md:h-full md:w-full object-contain"
                style={{ filter: 'drop-shadow(0 20px 60px rgba(34,211,238,0.28))' }}
                loading="lazy"
                decoding="async"
                data-testid="hammer-image"
              />
            </div>
          </Reveal>

          {/* Right: Feature cards */}
          <div className="md:col-span-8 md:pl-2 order-2">
            <ul className="space-y-4 sm:space-y-5">
              {FEATURES.map((f, idx) => {
                const Icon = f.icon;
                const num = String(idx + 1).padStart(2, '0');
                return (
                  <motion.li
                    key={f.title}
                    className="panel rounded-xl p-4 sm:p-5 flex items-start gap-4 sm:gap-5 group hover:border-cyan-400/30 transition-colors"
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.7, ease: EASE, delay: 0.35 + idx * 0.07 }}
                    data-testid={`hammer-feature-${idx}`}
                  >
                    <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-md bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-3">
                        <span className="font-tech text-[10px] tracking-[0.28em] text-cyan-400/70">
                          {num}
                        </span>
                        <h3 className="font-display text-white text-base sm:text-lg font-semibold leading-tight">
                          {f.title}
                        </h3>
                      </div>
                      <p className="mt-1.5 text-zinc-400 text-sm leading-relaxed">{f.body}</p>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        </div>

        <Reveal
          className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 justify-center md:justify-start md:pl-[calc(33.3333%+2.5rem)] lg:pl-[calc(33.3333%+3rem)]"
          delay={0.5}
        >
          <a
            href="#quote-form"
            onClick={() => track('buy_click', { location: 'hammer' })}
            data-testid="hammer-order-now-btn"
            className="btn-teal inline-flex items-center justify-center rounded-md px-5 sm:px-6 py-3 text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em] whitespace-nowrap"
          >
            Order Now&nbsp;·&nbsp;$10,500*
          </a>
          <a
            href="https://absolutehammers.com"
            onClick={() => track('buy_click', { location: 'hammer_info' })}
            data-testid="hammer-more-info-btn"
            className="inline-flex items-center justify-center rounded-md px-5 sm:px-6 py-3 text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em] whitespace-nowrap bg-transparent border border-cyan-400 text-cyan-300 hover:bg-cyan-400/10 hover:text-cyan-200 transition-colors"
          >
            Get More Info
          </a>
        </Reveal>
        <Reveal
          className="mt-3 text-center md:text-left md:pl-[calc(33.3333%+2.5rem)] lg:pl-[calc(33.3333%+3rem)] text-[11px] text-zinc-500"
          delay={0.6}
        >
          <span className="text-cyan-300">*</span> Price does not include shipping — quoted separately.
        </Reveal>
      </div>
    </section>
  );
}
